import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { logAppError } from "@/lib/appLogger";

const TOKEN_STORAGE_KEY = "go24_push_token";
let nativePushInitialized = false;

type NativePermissionReceive = "granted" | "denied" | "prompt" | "prompt-with-rationale";
type NativePermissionStatus = { receive: NativePermissionReceive };
type PushNotificationSchemaLike = {
  title?: string;
  body?: string;
  data?: Record<string, unknown>;
};
type ActionPerformedLike = {
  notification: PushNotificationSchemaLike;
};
type PushNotificationsPluginLike = {
  createChannel(options: {
    id: string;
    name: string;
    description: string;
    importance: number;
    visibility: number;
    sound: string;
  }): Promise<unknown>;
  checkPermissions(): Promise<NativePermissionStatus>;
  requestPermissions(): Promise<NativePermissionStatus>;
  register(): Promise<void>;
  addListener(
    eventName: "registration",
    listener: (token: { value: string }) => void,
  ): Promise<unknown> | unknown;
  addListener(
    eventName: "registrationError",
    listener: (error: unknown) => void,
  ): Promise<unknown> | unknown;
  addListener(
    eventName: "pushNotificationReceived",
    listener: (notification: PushNotificationSchemaLike) => void,
  ): Promise<unknown> | unknown;
  addListener(
    eventName: "pushNotificationActionPerformed",
    listener: (action: ActionPerformedLike) => void,
  ): Promise<unknown> | unknown;
};
type CapacitorLike = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
};

declare global {
  interface Window {
    Capacitor?: CapacitorLike;
  }
}

async function loadCapacitorModule(): Promise<CapacitorLike | null> {
  const globalCapacitor = typeof window !== "undefined" ? window.Capacitor : null;
  if (globalCapacitor) {
    return globalCapacitor;
  }

  try {
    const moduleName = "@capacitor/core";
    const capacitorModule = await import(/* @vite-ignore */ moduleName);
    return (capacitorModule.Capacitor as CapacitorLike | undefined) ?? null;
  } catch {
    return null;
  }
}

async function loadPushNotificationsPlugin(): Promise<PushNotificationsPluginLike | null> {
  try {
    const moduleName = "@capacitor/push-notifications";
    const pushModule = await import(/* @vite-ignore */ moduleName);
    return (pushModule.PushNotifications as PushNotificationsPluginLike | undefined) ?? null;
  } catch {
    return null;
  }
}

function getCapacitorGlobal(): CapacitorLike | null {
  if (typeof window === "undefined") return null;
  return window.Capacitor ?? null;
}

async function ensureLifecycleChannels() {
  if (!isNativePushEnvironment()) return;
  const pushNotifications = await loadPushNotificationsPlugin();
  if (!pushNotifications) return;

  const channelConfig = {
    name: "Go24 Lifecycle Updates",
    description: "Booking, inspection, payment, and handover alerts",
    importance: 5,
    visibility: 1,
    sound: "default",
  } as const;

  await pushNotifications.createChannel({
    id: "go24-lifecycle",
    ...channelConfig,
  }).catch(() => undefined);

  // Keep a fallback/default Android channel alive for FCM/native compatibility.
  await pushNotifications.createChannel({
    id: "default",
    ...channelConfig,
  }).catch(() => undefined);
}

function mapPermissionStatus(permission: NativePermissionReceive | NotificationPermission): NotificationPermission {
  if (permission === "granted") return "granted";
  if (permission === "denied") return "denied";
  return "default";
}

async function currentPlatform(): Promise<"android" | "ios" | "web"> {
  const capacitor = (await loadCapacitorModule()) ?? getCapacitorGlobal();
  if (!capacitor?.isNativePlatform?.()) return "web";
  const platform = capacitor.getPlatform?.() ?? "web";
  return platform === "ios" ? "ios" : "android";
}

function inferRoleFromPath(pathname = window.location.pathname): string | null {
  if (pathname.startsWith("/c")) return "customer";
  if (pathname.startsWith("/s")) return "supplier";
  if (pathname.startsWith("/dispatch") || pathname.startsWith("/d")) return "dispatch";
  if (pathname.startsWith("/p")) return "partner";
  return null;
}

function isUuidLike(value: string | undefined | null) {
  if (!value) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function fallbackRouteFromPayload(data: Record<string, string | undefined>) {
  const type = data.type ?? data.event ?? null;
  const dispatchTicketId = data.dispatch_ticket_id ?? null;

  if (type === "dispatch_test" || type === "inspection_completed") {
    return dispatchTicketId ? `/d/job/${dispatchTicketId}` : "/d/jobs";
  }

  if (type === "supplier_assigned" || type === "dispatch_approved") {
    return "/s/bookings";
  }

  if (type === "handover_ready") {
    if (dispatchTicketId) {
      return `/p/ticket/${dispatchTicketId}`;
    }

    return "/p";
  }

  if (type === "payment_required" && isUuidLike(data.order_id)) {
    return `/c/booking-flow?orderId=${data.order_id}`;
  }

  return null;
}

function routeFromNotification(data: Record<string, string | undefined>) {
  const route = data.route ?? data.target_path ?? null;
  if (route) {
    console.log("[DEEP LINK NAVIGATION]", route);
    window.location.assign(route);
    return;
  }

  const typedFallbackRoute = fallbackRouteFromPayload(data);
  if (typedFallbackRoute) {
    console.log("[DEEP LINK NAVIGATION]", typedFallbackRoute);
    window.location.assign(typedFallbackRoute);
    return;
  }

  if (isUuidLike(data.order_id)) {
    const fallbackRoute = `/c/booking-flow?orderId=${data.order_id}`;
    console.log("[DEEP LINK NAVIGATION]", fallbackRoute);
    window.location.assign(fallbackRoute);
    return;
  }

  console.log("[DEEP LINK NAVIGATION]", "/");
  window.location.assign("/");
}

async function persistToken(token: string) {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  const { data } = await supabase.auth.getSession();
  const session = data.session;
  if (!session) return;
  const userId = session.user.id;

  const { error } = await supabase.functions.invoke("register-device-token", {
    body: {
      userId,
      token,
      platform: await currentPlatform(),
      appRole: inferRoleFromPath(),
    },
  });

  if (error) {
    logAppError("push_token_register_failed", error, { token_preview: token.slice(0, 12) });
    return;
  }

  console.log("[DEVICE TOKEN STORED]", {
    userId,
    token,
  });
}

export async function syncStoredPushToken() {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) return;
  await persistToken(token);
}

export function isNativePushEnvironment() {
  return Boolean(getCapacitorGlobal()?.isNativePlatform?.());
}

export async function getPushPermissionState(): Promise<NotificationPermission> {
  if (isNativePushEnvironment()) {
    const pushNotifications = await loadPushNotificationsPlugin();
    if (!pushNotifications) return "default";
    const permission = await pushNotifications.checkPermissions();
    return mapPermissionStatus(permission.receive);
  }

  if ("Notification" in window) {
    return Notification.permission;
  }

  return "denied";
}

export async function requestPushPermissionAndRegister(): Promise<NotificationPermission> {
  if (isNativePushEnvironment()) {
    const pushNotifications = await loadPushNotificationsPlugin();
    if (!pushNotifications) return "default";
    await ensureLifecycleChannels();

    const permission = await pushNotifications.requestPermissions();
    const mapped = mapPermissionStatus(permission.receive);
    if (mapped === "granted") {
      await pushNotifications.register();
    }
    return mapped;
  }

  if (!("Notification" in window)) {
    return "denied";
  }

  return Notification.requestPermission();
}

export function showBrowserNotification(title: string, options?: NotificationOptions, permission?: NotificationPermission) {
  if (isNativePushEnvironment()) return;
  if (permission !== "granted") return;
  if (document.visibilityState === "visible") return;

  const notification = new Notification(title, {
    icon: "/icons/icon-192.png",
    badge: "/icons/icon-192.png",
    requireInteraction: true,
    ...options,
  });

  notification.onclick = () => {
    window.focus();
    notification.close();
  };

  setTimeout(() => notification.close(), 30000);
}

export function initNativePushNotifications() {
  if (!isNativePushEnvironment() || nativePushInitialized) return;
  nativePushInitialized = true;

  void (async () => {
    const pushNotifications = await loadPushNotificationsPlugin();
    if (!pushNotifications) {
      nativePushInitialized = false;
      return;
    }

    await ensureLifecycleChannels();

    pushNotifications.addListener("registration", (token) => {
      console.log("[FCM TOKEN]", token.value);
      void persistToken(token.value);
    });

    pushNotifications.addListener("registrationError", (error) => {
      logAppError("push_registration_error", error);
    });

    pushNotifications.addListener("pushNotificationReceived", (notification: PushNotificationSchemaLike) => {
      console.log("[PUSH RECEIVED - FOREGROUND]", notification);
      const data = (notification.data ?? {}) as Record<string, string | undefined>;
      const title = notification.title || "Go24 Sub";
      const description = notification.body || "Open the app to review the latest update.";

      toast(title, {
        description,
        action: {
          label: "Open",
          onClick: () => routeFromNotification(data),
        },
      });
    });

    pushNotifications.addListener("pushNotificationActionPerformed", (action: ActionPerformedLike) => {
      console.log("[PUSH CLICKED]", action.notification.data);
      const data = (action.notification.data ?? {}) as Record<string, string | undefined>;
      routeFromNotification(data);
    });
  })();
}
